/**
 * In-page HatGPT mini chat for the product page (RIS-685).
 *
 * Frames nms-create's /embed/pdp-chat and bridges it to this page. The page owns three
 * things the framed document cannot do for itself:
 *   - loading: the markup's baseline is a plain link (no JS, or this script failing to load,
 *     still leaves a working path). Running, this script shows a short "loading" placeholder
 *     and creates the iframe once the page has finished loading and is idle, so the chat
 *     never delays the product page's own load;
 *   - height: the frame is auto-height, sized from the child's postMessage;
 *   - scrolling: the framed document never scrolls, so it asks us to scroll the page.
 *
 * Protocol v1, origin-checked in BOTH directions (see nms-create static/js/embed_pdp_chat.js):
 *   parent -> child  nms-embed:hello  (handshake: lets the chat identify this page when the
 *                    browser gives it no ancestor origin or referrer, e.g. Firefox)
 *   child -> parent  nms-embed:ready  {height}
 *                    nms-embed:height {height}
 *                    nms-embed:reveal {replyTop, resultsTop, inputTop}   (px in the frame)
 *
 * Every incoming message must come from the iframe's own window AND the embed's origin,
 * carry v === 1, and have finite, bounded numbers. Anything else is ignored. If the chat
 * has not reported ready within data-load-timeout the plain link is shown and the frame
 * keeps loading out of sight; if it reports ready later it replaces the link.
 *
 * No `sandbox` on the frame, on purpose: the chat's add-to-cart navigates the top window
 * (form target=_top) and any sandbox that allowed that would allow most of what it blocks.
 */
(function () {
  'use strict';

  var MAX_HEIGHT = 6000;
  var HELLO_RETRIES = 8;
  var HELLO_EVERY_MS = 800;
  var START_FAILSAFE_MS = 6000;
  var REVEAL_MIN_GAP_MS = 250;

  function finite(n) { return typeof n === 'number' && isFinite(n); }
  // Positions inside the frame's document, in px. Slightly negative is legitimate (the
  // child subtracts a margin from the reply's top); anything outside the frame is not.
  function inFrame(n) { return finite(n) && n >= -100 && n <= MAX_HEIGHT; }

  function init(root) {
    if (!root || root.__pdpHatgpt) return;
    root.__pdpHatgpt = true;

    var src = root.getAttribute('data-src');
    var facade = root.querySelector('[data-facade]');
    var mount = root.querySelector('[data-mount]');
    var fallback = root.querySelector('[data-fallback]');
    var timeoutMs = parseInt(root.getAttribute('data-load-timeout'), 10) || 15000;
    if (!src || !mount) return;       // markup without a frame URL keeps its plain link

    var origin;
    try { origin = new URL(src, window.location.href).origin; } catch (e) { return; }

    var frame = null;
    var timer = null;
    var helloTimer = null;
    var ready = false;
    var lastReveal = 0;

    // The markup's baseline (no JS, or this script failing to load) is the plain link.
    // Running means we take over: show the short "loading" placeholder, hide the link.
    if (facade) facade.hidden = false;
    if (fallback) fallback.hidden = true;

    function postToChild(type) {
      if (!frame || !frame.contentWindow) return;
      try { frame.contentWindow.postMessage({ type: type, v: 1 }, origin); } catch (e) { /* ignore */ }
    }

    function track(name) {
      try {
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({ event: name });
        }
      } catch (e) { /* analytics never gates the chat */ }
    }

    // A slow chat (a cold Cloud Run start can take ~13s) is not a dead one: keep the frame
    // loading out of sight behind the link, and if it reports ready later, show it after all.
    function fail() {
      root.classList.remove('is-loading');
      root.classList.add('is-failed');
      if (facade) facade.hidden = true;
      if (fallback) fallback.hidden = false;
      track('pdp_hatgpt_embed_failed');
    }

    // The chat may not be able to tell who its parent is (Firefox has no ancestorOrigins;
    // a strict referrer policy removes the referrer), so introduce ourselves once it has
    // loaded, and keep trying briefly until it reports ready.
    function startHello() {
      postToChild('nms-embed:hello');
      var tries = 0;
      clearInterval(helloTimer);
      helloTimer = setInterval(function () {
        if (ready || !frame || ++tries > HELLO_RETRIES) { clearInterval(helloTimer); return; }
        postToChild('nms-embed:hello');
      }, HELLO_EVERY_MS);
    }

    function load() {
      if (frame) return;
      root.classList.remove('is-failed');
      if (fallback) fallback.hidden = true;
      if (facade) facade.hidden = false;
      frame = document.createElement('iframe');
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.src = src;
      frame.title = 'HatGPT: customize your own hat';
      frame.setAttribute('scrolling', 'no');
      frame.setAttribute('allow', '');
      frame.style.height = '0px';
      frame.addEventListener('load', startHello);
      mount.hidden = false;
      mount.appendChild(frame);
      root.classList.add('is-loading');
      timer = setTimeout(function () { if (!ready) fail(); }, timeoutMs);
    }

    function setHeight(h) {
      if (!frame || !finite(h) || h <= 0 || h > MAX_HEIGHT) return;
      frame.style.height = Math.ceil(h) + 'px';
    }

    // Mirrors chatbot.js revealResults, in PARENT coordinates: stop with the latest reply
    // near the top, then the text box, then the hat; never pull the page up from where the
    // shopper is unless they have scrolled past the results.
    function reveal(d) {
      if (!frame) return;
      var now = Date.now();
      if (now - lastReveal < REVEAL_MIN_GAP_MS) return;     // a buggy chat build must not scroll-hijack
      var vh = window.innerHeight;
      var frameTop = frame.getBoundingClientRect().top + window.scrollY;
      var resultsAbs = inFrame(d.resultsTop) ? frameTop + d.resultsTop : null;
      if (resultsAbs === null) return;
      lastReveal = now;       // only a well-formed reveal uses up the window: junk must not block a real one
      var replyAbs = inFrame(d.replyTop) ? frameTop + d.replyTop : null;
      var inputAbs = inFrame(d.inputTop) ? frameTop + d.inputTop : null;

      var boxTop = resultsAbs - window.scrollY;
      if (boxTop >= 0 && boxTop < vh * 0.6) return;            // already in view

      var target = resultsAbs - vh * 0.45;
      if (replyAbs !== null && resultsAbs - replyAbs <= vh * 0.75) target = replyAbs;
      if (inputAbs !== null) target = Math.min(target, inputAbs - 12);
      target = Math.max(0, target);
      if (target <= window.scrollY && boxTop >= 0) return;

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
    }

    function onMessage(e) {
      // The theme editor re-renders the section in place; this root is then gone, so
      // retire the listener instead of leaking one per render.
      if (!root.isConnected) {
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        clearInterval(helloTimer);
        return;
      }
      if (!frame || e.source !== frame.contentWindow || e.origin !== origin) return;
      var d = e.data;
      if (!d || d.v !== 1 || typeof d.type !== 'string') return;

      if (d.type === 'nms-embed:ready') {
        ready = true;
        clearTimeout(timer);
        clearInterval(helloTimer);
        setHeight(d.height);
        root.classList.remove('is-loading', 'is-failed');
        root.classList.add('is-ready');
        if (facade) facade.hidden = true;
        if (fallback) fallback.hidden = true;
      } else if (d.type === 'nms-embed:height') {
        setHeight(d.height);
      } else if (d.type === 'nms-embed:reveal') {
        reveal(d);
      }
    }
    window.addEventListener('message', onMessage);

    // The chat is the section, not something behind a button: load it once the page is
    // idle. An iframe created before window.onload holds that event open until the framed
    // page finishes (measured 17s on a cold dev start), which delays the product page's own
    // load metrics, so start after load. If load itself never comes (a hung third-party
    // script), start anyway after a few seconds.
    function start() {
      if (window.requestIdleCallback) window.requestIdleCallback(load, { timeout: 400 });
      else setTimeout(load, 200);
    }
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    setTimeout(load, START_FAILSAFE_MS);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-pdp-hatgpt]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
